import { computed, ref } from "vue";
import { defineStore, storeToRefs } from "pinia";
import useMyFetch from "@stores/useMyFetch";
import { deepCopy, stripTokens } from "@utils/toolBox";
import genToken from "@utils/generateToken";
import handleError from "@composables/handleError";
import { useMCampaignStore } from "@stores/mCampaign";

export const useMAudiencesStore = defineStore("mAudiences", () => {
  const mCampaignStore = useMCampaignStore();
  const { currentMCampaign } = storeToRefs(mCampaignStore);

  const getMAudiences = computed(() => currentMCampaign.value.m_audiences);
  const activeMAudienceOptions = computed(() =>
    getMAudiences.value.filter((a) => a.status === "active").map((a) => ({ label: a.name, value: a.id })),
  );

  const _selectedAudience = ref({});
  const _selectedAudienceIndex = ref(-1);

  const selectAudience = (index) => {
    if (index == null) return;

    _selectedAudienceIndex.value = index;
    _selectedAudience.value = getMAudiences.value[index];
  };

  const addAudience = async () => {
    const { response, data } = await useMyFetch(
      `/m_campaigns/${currentMCampaign.value.token}/m_audiences/new`,
    ).json();

    if (response.value.ok) {
      const newAudience = { ...data.value, name: `Audience ${getMAudiences.value.length + 1}` };
      getMAudiences.value.push(newAudience);
      selectAudience(getMAudiences.value.length - 1);
    } else {
      handleError(data);
    }
  };

  const removeAudience = (index) => {
    getMAudiences.value.splice(index, 1);
    const newIndex = Math.min(index, getMAudiences.value.length - 1);
    selectAudience(newIndex);
  };

  const duplicateAudience = (index) => {
    const copyOfOriginalAudience = deepCopy(getMAudiences.value[index]);
    const duplicatedAudience = {
      ...copyOfOriginalAudience,
      m_dimensions: stripTokens(copyOfOriginalAudience.m_dimensions).map((mDimension) => ({
        ...mDimension,
        m_segments: stripTokens(mDimension.m_segments),
      })),
      age_brackets: stripTokens(copyOfOriginalAudience.age_brackets),
      name: `${copyOfOriginalAudience.name} Copy`,
      token: genToken(),
      id: null,
      pushed: false,
    };
    const newIndex = getMAudiences.value.length;
    getMAudiences.value.splice(newIndex, 0, duplicatedAudience);
    selectAudience(newIndex);
  };

  const updateAudienceStatus = (index, status) => {
    getMAudiences.value[index].status = status;
  };

  const isAudienceSelected = (index) => _selectedAudienceIndex.value === index;
  const isNewAudience = (audience) => audience.id === null;
  const isRemovableAudience = (audience) => !audience.pushed;

  return {
    // state
    _selectedAudience,
    // getters
    getMAudiences,
    activeMAudienceOptions,
    // actions
    addAudience,
    selectAudience,
    removeAudience,
    duplicateAudience,
    updateAudienceStatus,
    isAudienceSelected,
    isNewAudience,
    isRemovableAudience,
  };
});
